/* eslint-disable jsx-a11y/label-has-associated-control */
import { ChangeEvent } from "react";

import {
  Badge,
  Button,
  Card,
  FileInput,
  FontIcon,
  Spinner,
  Switch,
} from "@ezmid/components-library";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { FieldErrors } from "react-hook-form";

import { FormInputField } from "../../../components/hookFormComponents/FormInputField";
import {
  Option,
  SelectInputField,
} from "../../../components/hookFormComponents/SelectInputField";

const DEFAULT_IMAGE = "/avatar.svg";

type Props = {
  handleSubmit: any;
  control: any;
  errors: FieldErrors;
  userImage: string;
  onChangePicture: (e: ChangeEvent<HTMLInputElement>) => void;
  badgeLabel: string;
  isImageLoading: boolean;
  defaultRoleValue: Option;
  roleOptions: Array<Option>;
  isSubmitButtonDisabled: boolean;
  onSumbit: () => void;
  status: string;
  isUserDeactivated: boolean;
  onUserDeactivation: (event: React.ChangeEvent<HTMLInputElement>) => void;
  blockedFeatures: string[];
  onBlockedFeaturesChange: (feature: string) => void;
  allFeatureKeys: string[];
};

export function UserDetailForm({
  handleSubmit,
  control,
  errors,
  userImage,
  onChangePicture,
  badgeLabel,
  isImageLoading,
  defaultRoleValue,
  roleOptions,
  isSubmitButtonDisabled,
  onSumbit,
  status,
  isUserDeactivated,
  onUserDeactivation,
  blockedFeatures,
  onBlockedFeaturesChange,
  allFeatureKeys,
}: Props) {
  const badgeStatus = isUserDeactivated ? "danger" : "success";

  return (
    <Card
      shadows="lg"
      size="fill"
      headerText={t("user.personal.information")}
      hasBorder={false}
      headerIcon={
        <FontIcon
          marginTop={4}
          customIcon={<FontAwesomeIcon icon={faInfoCircle} />}
        />
      }
      headerItem={
        <div className="flex justify-between flex-row-reverse">
          <div className="flex pl-2">
            <Badge variant="pill">{badgeLabel}</Badge>
          </div>
          <Badge variant="pill" status={badgeStatus}>
            {status}
          </Badge>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="flex px-4 py-4">
        <div className="flex justify-center items-start w-3/12">
          {!isImageLoading ? (
            <div className="flex w-full min-w-full flex-col justify-start pr-2">
              <img
                src={userImage || DEFAULT_IMAGE}
                alt="profile"
                className=" rounded-md h-52 object-cover"
              />
            </div>
          ) : (
            <div className="flex justify-center min-w-full">
              <Spinner
                isLoading={isImageLoading}
                type="fadeLoader"
                size="20"
                color="#38bdf8"
              />
            </div>
          )}
        </div>
        <div className="w-3/4">
          <div className="flex justify-end pt-2">
            <Switch
              isChecked={isUserDeactivated}
              onChange={onUserDeactivation}
              variant="red"
              label={t("user.card.deactivate.user.label")}
            />
          </div>
          <FormInputField
            name="name"
            htmlFor="name"
            label={t("form.name.label")}
            id="name"
            type="text"
            control={control}
            error={errors.name}
            placeholder={t("form.name.label")}
          />
          <FormInputField
            name="email"
            htmlFor="email"
            label={t("form.email.label")}
            id="email"
            type="text"
            control={control}
            isDisabled
            error={errors.email}
            placeholder={t("form.email.label")}
          />
          <SelectInputField
            name="role"
            htmlFor="role"
            label="Role"
            options={roleOptions || []}
            control={control}
            defaultValue={defaultRoleValue}
          />
          <div className="pt-2">
            <label className="block font-normal mb-1 text-gray-500">
              {t("form.label.blockedFeatures")}
            </label>
            <div className="flex flex-col gap-2 pt-1">
              {allFeatureKeys.map((feature) => (
                <Switch
                  key={feature}
                  isChecked={blockedFeatures.includes(feature)}
                  onChange={() => onBlockedFeaturesChange(feature)}
                  variant="red"
                  label={t(`feature.${feature}`)}
                />
              ))}
            </div>
          </div>
          <div className="pt-2">
            <label className="block font-normal tmb-1 text-gray-500">
              {t("form.label.upload.picture")}
            </label>
            <FileInput inputSize="md" onChange={onChangePicture} />
          </div>

          <div className="flex justify-end mt-4">
            <Button
              size="md"
              onClick={onSumbit}
              disabled={isSubmitButtonDisabled}
            >
              {t("button.save")}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}
